<template>
  <div class="space-y-3" data-testid="bulk-invite-panel">
    <template v-if="!response">
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Email addresses</label>
        <Textarea
          v-model="rawEmails"
          rows="5"
          class="w-full text-sm"
          placeholder="Paste addresses — one per line, or separated by commas, semicolons or spaces"
          data-testid="textarea-bulk-invite-emails"
        />
        <p v-if="parsed.all.length" class="mt-1 text-xs text-gray-500" data-testid="bulk-invite-parsed-chip">
          {{ parsed.valid.length }} valid · {{ parsed.invalid.length }} invalid · {{ parsed.duplicates.length }} duplicates
        </p>
      </div>

      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Role for the batch (optional)</label>
        <Select
          v-model="selectedRoleId"
          :options="roleOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Role (optional)"
          showClear
          :loading="loadingRoles"
          class="w-full text-sm"
          data-testid="bulk-invite-role-select"
        />
      </div>

      <div v-if="overCap" class="flex items-center gap-1.5 text-amber-700 text-xs bg-amber-50 rounded-lg px-2.5 py-1.5" data-testid="bulk-invite-cap-warning">
        <i class="pi pi-exclamation-triangle"></i>
        <span>A bulk invite is limited to 100 addresses per batch — remove {{ parsed.all.length - 100 }} before sending.</span>
      </div>
      <div v-if="error" class="flex items-center gap-1.5 text-red-600 text-xs bg-red-50 rounded-lg px-2.5 py-1.5">
        <i class="pi pi-exclamation-circle"></i>
        <span>{{ error.message }}</span>
      </div>

      <div class="flex justify-end">
        <Button
          :label="sendLabel"
          icon="pi pi-send"
          size="small"
          :loading="loading"
          :disabled="!canSubmit"
          @click="send"
          data-testid="btn-send-bulk-invitations"
        />
      </div>
    </template>

    <template v-else>
      <div class="flex items-center gap-1.5 text-emerald-600 text-xs bg-emerald-50 rounded-lg px-2.5 py-1.5" data-testid="bulk-invite-summary">
        <i class="pi pi-check-circle"></i>
        <span>{{ response.summary.sent }} sent · {{ response.summary.skipped }} skipped of {{ response.summary.total }}</span>
      </div>

      <div class="max-h-64 overflow-y-auto border border-gray-100 rounded-lg">
        <table class="w-full text-xs" data-testid="bulk-invite-result-table">
          <thead class="sticky top-0 bg-gray-50 text-gray-500">
            <tr>
              <th class="text-left font-medium px-2.5 py-1.5">Email</th>
              <th class="text-left font-medium px-2.5 py-1.5">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in response.results" :key="`${row.email}-${index}`" class="border-t border-gray-100">
              <td class="px-2.5 py-1.5 text-gray-700">{{ row.email }}</td>
              <td class="px-2.5 py-1.5">
                <Tag :value="statusLabel(row.status)" :severity="statusSeverity(row.status)" class="text-[10px]" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex justify-end">
        <Button
          label="Invite more"
          icon="pi pi-plus"
          size="small"
          severity="secondary"
          outlined
          @click="reset"
          data-testid="btn-bulk-invite-more"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useBulkInviteStaff } from '@/composables/useBulkInviteStaff';
import { useRoleOptions } from '@/composables/useRoleOptions';
import { parseBulkEmails } from '@/utils/parseBulkEmails';
import { BulkInviteStatus } from '@/types/composition-functions';
import type { BulkInviteStaffMutation } from '@/types/operations';

const MAX_ADDRESSES = 100;

const rawEmails = ref('');
const selectedRoleId = ref<string | null>(null);
const response = ref<BulkInviteStaffMutation['bulkInviteStaff'] | null>(null);

const parsed = computed(() => parseBulkEmails(rawEmails.value));
const overCap = computed(() => parsed.value.all.length > MAX_ADDRESSES);
const canSubmit = computed(() => parsed.value.valid.length > 0 && !overCap.value);
const sendLabel = computed(() =>
  parsed.value.valid.length > 1 ? `Send ${parsed.value.valid.length} Invitations` : 'Send Invitation',
);

// Same available-roles source as the single-invite role picker; one role applies
// to the whole batch, or none to keep today's request-a-role-later behaviour.
const { roleOptions, loadingRoles } = useRoleOptions();

const { bulkInviteStaff, loading, error, onDone } = useBulkInviteStaff();

const send = async() => {
  if (!canSubmit.value || loading.value) return;
  await bulkInviteStaff({
    input: {
      emails: parsed.value.all,
      school_permission_group_id: selectedRoleId.value || null,
    },
  });
};

onDone((result) => {
  response.value = result.data?.bulkInviteStaff ?? null;
});

const reset = () => {
  response.value = null;
  rawEmails.value = '';
};

const STATUS_META: Record<BulkInviteStatus, { label: string; severity: string }> = {
  [BulkInviteStatus.Sent]: { label: 'Sent', severity: 'success' },
  [BulkInviteStatus.SkippedDuplicateInBatch]: { label: 'Duplicate in batch', severity: 'secondary' },
  [BulkInviteStatus.SkippedAlreadyInvited]: { label: 'Already invited', severity: 'warn' },
  [BulkInviteStatus.SkippedAlreadyRegistered]: { label: 'Already registered', severity: 'info' },
  [BulkInviteStatus.SkippedInvalidFormat]: { label: 'Invalid format', severity: 'danger' },
};

const statusLabel = (status: BulkInviteStatus) => STATUS_META[status]?.label ?? status;
const statusSeverity = (status: BulkInviteStatus) => STATUS_META[status]?.severity ?? 'secondary';
</script>
